import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, fmtMoney } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import { FileText } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";

const monthName = (m) => ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"][m-1];

export default function PayslipsPage() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [params] = useSearchParams();
  const runFilter = params.get("run");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/payslips", { params: runFilter ? { payroll_run_id: runFilter } : {} });
      setRows(data);
    } catch (e) { toast.error("Failed"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [runFilter]);

  return (
    <div>
      <PageHeader
        testid="page-payslips"
        eyebrow="Records"
        title="Payslips."
        description={user.role === "employee" ? "Your payslip history. Click any to view or print." : "Every payslip your firm has issued for this client."}
      />

      <div className="p-8">
        <div className="border border-neutral-200 bg-white overflow-x-auto">
          <table className="w-full text-sm" data-testid="payslips-table">
            <thead className="bg-[#FAFAFA] border-b border-neutral-200">
              <tr className="text-left font-mono text-[10px] uppercase tracking-widest text-neutral-500">
                <th className="px-4 py-3">Period</th>
                {user.role !== "employee" && <th className="px-4 py-3">Employee</th>}
                <th className="px-4 py-3 text-right">Gross</th>
                <th className="px-4 py-3 text-right">Deductions</th>
                <th className="px-4 py-3 text-right">Net</th>
                <th className="px-4 py-3 text-right"></th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={6} className="p-6 text-neutral-500 font-mono">Loading…</td></tr>}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={6} className="p-8 text-neutral-500 font-mono">No payslips available.</td></tr>
              )}
              {rows.map((r) => (
                <tr key={r.id} className="border-b border-neutral-100 hover:bg-[#FAFAFA] transition-colors" data-testid={`row-slip-${r.id}`}>
                  <td className="px-4 py-3 font-semibold">{monthName(r.month)} {r.year}</td>
                  {user.role !== "employee" && (
                    <td className="px-4 py-3">
                      <div className="font-semibold">{r.employee_name}</div>
                      <div className="text-xs text-neutral-500 font-mono">{r.employee_code} · {r.department}</div>
                    </td>
                  )}
                  <td className="px-4 py-3 font-mono text-right">{fmtMoney(r.gross)}</td>
                  <td className="px-4 py-3 font-mono text-right text-[#FF3B30]">{fmtMoney(r.total_deductions)}</td>
                  <td className="px-4 py-3 font-mono text-right font-semibold text-[#002FA7]">{fmtMoney(r.net)}</td>
                  <td className="px-4 py-3 text-right">
                    <Link to={`/app/payslips/${r.id}`} data-testid={`view-slip-${r.id}`} className="inline-flex items-center gap-1 font-mono text-xs hover:text-[#002FA7] transition-colors">
                      <FileText className="w-3.5 h-3.5" /> View
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
